/**
 * Raw, framework-agnostic color primitives — the single source of truth for
 * every product (Government Portal web, Web/marketing, Admin, and Government Portal mobile).
 *
 * This file has ZERO dependencies (no MUI, no React Native) so it can be
 * imported from any runtime. Web derives its MUI palette from these values in
 * `libs/ui` (adding alpha-based variants there); mobile maps these onto its
 * react-native-paper theme. Do not hardcode hex literals in app code — add or
 * reference a token here.
 */
/**
 * A portal's accent family.
 *
 * Every portal shares the same cream page rest, the same hairline borders, the
 * same ink and the same type. What separates them is this: one accent hue and
 * the dark chrome derived from it. That is deliberate — an officer, a property
 * owner and an operator all see one product, and the hue is what tells them
 * which room of it they are standing in.
 *
 * Every family below clears WCAG 2.1 AA on the surfaces it is actually used on,
 * and `contrast.spec.ts` gates all three rather than just the default one:
 *  - `accent` / `accentDark` as ink (and as a fill under white text) on white,
 *    cream, cream2, parch and their own tint — 4.5:1
 *  - `accentLight` / `accentLightHover` as ink on all three rails — 4.5:1
 *  - white and `text.mutedOnDark` on all three rails — 4.5:1
 *  - `accentMute` as a non-text stroke on the rails — 3:1
 */
export interface PortalAccent {
  /** Primary accent for use on light backgrounds — buttons, links, active fills. */
  accent: string;
  /** Bright accent for use on the dark rail — logo marks, focus rings, icons. */
  accentLight: string;
  /** Hover for a control filled with `accentLight`. */
  accentLightHover: string;
  /** Pressed / active accent on light surfaces. */
  accentDark: string;
  /** Soft accent fill — selected-row / hover background on light surfaces. */
  accentTint: string;
  /** Muted accent-grey for outline strokes on dark surfaces. */
  accentMute: string;
  /** The portal's dark chrome: sidenav rail, header, footer. */
  rail: string;
  /** Hover / emphasis step darker than the rail. */
  railHover: string;
  /** Device-frame / abyss surface, the darkest step. */
  railAbyss: string;
}

/**
 * The three portal accent families.
 *
 * `gov` is the original brand teal, unchanged — the Government Portal is the
 * flagship and its colour is the one the brand was drawn around. `public` (the
 * Client Portal, for property owners and occupants) and `admin` (operator
 * tooling) are the same lightness at a different hue, so nothing reads as
 * louder or more important than anything else; only the room changes.
 */
export const portalAccents = {
  /**
   * Government Portal — brand teal.
   *
   * WCAG 2.1 AA (accessibility pass): `accent` was `#1e8a8a`, which scored
   * 4.15:1 against white and 3.85:1 against cream, so both white-on-teal (the
   * primary button) and teal-as-text failed 1.4.3 for normal-size text.
   * Darkened the same hue to clear 4.5:1 on every light surface while staying
   * visually the brand teal.
   */
  gov: {
    accent: '#0e7373',
    accentLight: '#2ab8b8',
    accentLightHover: '#41c9c8',
    accentDark: '#0f6464',
    accentTint: '#e6f3f3',
    accentMute: '#c9d2d1',
    rail: '#143d3d',
    railHover: '#0e2c2d',
    railAbyss: '#0a2120',
  },
  /**
   * Client Portal — deep indigo.
   *
   * Replaced the earlier purple (`#6b4899`, rail `#2e1f47`) at the client's
   * request. Hue 232: in the same blue family as the admin portal but 41
   * degrees past its petrol blue and 52 past the gov teal, so the three rails
   * still read as three rooms — green-grey, blue-grey, ink-indigo. It sits 25
   * degrees from `semantic.pending` (hue 207), with far more saturation, so a
   * pending chip on a Client Portal page still reads as a status, not chrome.
   * Kept deep on purpose: a sky blue would neither clear AA as text nor carry
   * the weight of an enforcement notice. A property owner who also holds an
   * officer login should never have to check the URL to know which side of an
   * enforcement file they are looking at.
   */
  public: {
    accent: '#4351ad',
    accentLight: '#9aa6f0',
    accentLightHover: '#b0baf4',
    accentDark: '#384494',
    accentTint: '#eaecf8',
    accentMute: '#cbcedf',
    rail: '#1e2452',
    railHover: '#161a3e',
    railAbyss: '#0e112b',
  },
  /**
   * Admin portal — petrol blue.
   *
   * The blue end of blue-green, at hue 191. An earlier pass sat at 167 to open
   * up the gap to the gov teal and simply read as forest green instead, which
   * is the wrong answer to the right question: the gap matters less than the
   * colour being the one that was asked for. It is 11 degrees off the teal, so
   * the two are close cousins — the admin rail reads as a blue-grey where the
   * Government Portal's reads green — and the wide separation in this system is
   * carried by the Client Portal's indigo.
   */
  admin: {
    accent: '#0a5f73',
    accentLight: '#38b4d0',
    accentLightHover: '#55c2da',
    accentDark: '#08505f',
    accentTint: '#e3eff3',
    accentMute: '#c4d3d9',
    rail: '#0c2e38',
    railHover: '#08212a',
    railAbyss: '#05161d',
  },
} as const satisfies Record<string, PortalAccent>;

/** Which portal an accent family belongs to. */
export type PortalKey = keyof typeof portalAccents;

export const colors = {
  /**
   * The default accent family — the Government Portal's teal.
   *
   * `colors.brand` is what a surface that belongs to no particular portal
   * reads (the marketing site, the mobile app, shared components rendered
   * outside a portal shell). A portal shell reads its own family from
   * `portalAccents` instead.
   */
  brand: portalAccents.gov,
  text: {
    primary: '#111111',
    secondary: '#4a5568',
    /**
     * Tertiary/meta text on light surfaces. Was `#9aa5b4` (2.1–2.5:1) — a hard
     * 1.4.3 failure everywhere it appeared. Darkened to clear 4.5:1 on white,
     * cream, cream2, parch and every portal's accent tint.
     */
    muted: '#616a76',
    /** Muted text when set against a portal rail. */
    mutedOnDark: '#9aa6a4',
  },
  surface: {
    /** Cream page rest */
    cream: '#f8f6f2',
    /** Second-step cream — neutral chip fills, pressed cream surfaces */
    cream2: '#f1ede4',
    white: '#ffffff',
    /** Parchment accent zone */
    parch: '#efe9dc',
  },
  border: {
    onCream: '#e6e3dc',
    onWhite: '#e2e8ee',
    /** Hairline rule on cream / white surfaces */
    line: '#dcd8cf',
    /** Stronger hairline — input borders, secondary-button outline */
    lineStrong: '#cfcabe',
    /** Subtle outer ring of a brandmark on a light surface */
    onLight: '#e0e0e0',
    /**
     * Boundary of a *focusable* control (input outline, secondary-button edge).
     * WCAG 1.4.11 requires 3:1 for the visual information that identifies a UI
     * component, which the decorative hairlines above do not reach — they stay
     * as they are for dividers and card edges, and this token carries the
     * interactive boundaries. 3.40:1 on white, 3.15:1 on cream.
     */
    interactive: '#8f8b81',
    /**
     * Focus indicator on light surfaces (2.4.11 / 2.4.13), for a surface with
     * no portal of its own. A portal shell overrides this with its own
     * `accent`, which clears 3:1 against white, cream and the tints in all
     * three families.
     */
    focus: '#0e7373',
    /**
     * Focus indicator on dark chrome, likewise overridden per portal with that
     * portal's `accentLight` — 4.9:1 or better against every rail.
     */
    focusOnDark: '#2ab8b8',
  },
  semantic: {
    emergency: '#b8362e',
    /**
     * Was `#b87a1f` — 3.6:1 on white, 2.9:1 on its own `warningTint`, so both
     * warning-as-text and white-on-warning chips failed 1.4.3. Darkened to
     * clear 4.5:1 on every light surface including `warningTint`.
     */
    warning: '#8f5d10',
    pending: '#285f8c',
    success: '#2c6e4b',
    /** Sub-flow paused (e.g. entry-warrant) */
    paused: '#6b5d3f',
    /** File closed / archived — darkened from `#6e6e6e` (4.3:1 on `closedTint`). */
    closed: '#5f5f5f',
    emergencyTint: '#f3dedb',
    warningTint: '#f3e6d2',
    pendingTint: '#e6eef6',
    successTint: '#e3efe7',
    pausedTint: '#f3ecd9',
    closedTint: '#ececea',
    /** Darker readable ink for text/labels on the matching tint */
    emergencyInk: '#5c1a16',
    warningInk: '#5c3a0c',
    /** Hairline border for the emergency banner surface (on emergencyTint) */
    emergencyBorder: '#f0c8c4',
  },
  common: {
    white: '#ffffff',
    black: '#000000',
  },
  /**
   * The dark scheme's own grounds and inks — the mobile app's Appearance →
   * Dark preference (#288). Deep teal-black surfaces derived by hand from the
   * brand rail; everything else in the dark scheme (the accent, the muted
   * ink, the status hues) reuses the light tokens above. They lived as raw hex
   * in the mobile app until the "no hex in app code" rule caught up with them;
   * `contrast.spec.ts` gates them here, and mobile's own
   * `src/ui/contrast.test.ts` gates them as actually rendered.
   */
  dark: {
    surface: {
      /** Page rest. */
      cream: '#0b1716',
      /** Second-step ground — pressed and neutral-chip fills. */
      cream2: '#1b2c2a',
      /** Card. */
      white: '#12211f',
    },
    /** Primary ink on the dark grounds. */
    text: '#eef3f1',
    /** Selected-row / active fill. */
    accentTint: '#173e3e',
    border: {
      /** Hairline. */
      line: '#22302e',
      /** Input / secondary-button outline. */
      lineStrong: '#2b3c39',
    },
  },
  /**
   * Ink for marking up evidence photos (arrows, circles, callouts). Saturated
   * on purpose: they are drawn over arbitrary photographs, not over the page,
   * so they are chosen to read on foliage, siding and sky rather than to clear
   * a text-contrast floor. Keyed by the names `MARKUP_COLOURS` in
   * contracts-gov records, so a marked-up copy says which ink it used.
   */
  markup: {
    red: '#e0301e',
    yellow: '#f5c400',
    blue: '#1f6fe0',
    white: '#ffffff',
    black: '#000000',
  },
  /**
   * Decorative gradient stops. `auth` is the sign-in screens' warm parchment
   * radial wash (the "Quiet paper" handoff), deepest at the bottom edge; it
   * ends on `surface.cream`, which the caller appends.
   */
  wash: {
    auth: ['#f0e2c4', '#f6ecd5', '#f7f0e0'],
  },
} as const;

export type Colors = typeof colors;
